import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  ArrowsRightLeftIcon,
  ArrowUpTrayIcon,
  TagIcon,
  WalletIcon,
  ClipboardDocumentListIcon,
  UserIcon,
  ShieldCheckIcon,
  ArrowRightOnRectangleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import AccountSwitcher from './AccountSwitcher';

function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, accountContextVersion, activeAccount } = useAuth();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Your account';
  const initials = `${String(user?.firstName || '').slice(0, 1)}${String(user?.lastName || '').slice(0, 1)}`
    .toUpperCase()
    || 'U';

  const handleOpenProfile = () => {
    setIsUserMenuOpen(false);
    navigate('/profile');
  };

  const handleOpenCategories = () => {
    setIsUserMenuOpen(false);
    navigate('/categories');
  };

  const handleOpenSecurity = () => {
    setIsUserMenuOpen(false);
    navigate('/forgot-password');
  };

  const handleOpenSharing = () => {
    setIsUserMenuOpen(false);
    navigate('/settings/sharing');
  };

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    logout();
    navigate('/login');
  };

  useEffect(() => {
    const handleOutsidePress = (event) => {
      if (!userMenuRef.current) {
        return;
      }
      if (!userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    window.addEventListener('mousedown', handleOutsidePress);
    window.addEventListener('touchstart', handleOutsidePress);
    return () => {
      window.removeEventListener('mousedown', handleOutsidePress);
      window.removeEventListener('touchstart', handleOutsidePress);
    };
  }, []);

  const navLinks = [
    { path: '/dashboard', label: 'Dashboard', mobileLabel: 'Home', icon: HomeIcon },
    { path: '/transactions', label: 'Transactions', mobileLabel: 'Trans', icon: ArrowsRightLeftIcon },
    { path: '/upload', label: 'Upload Receipt', mobileLabel: 'Upload', icon: ArrowUpTrayIcon },
    { path: '/budget', label: 'Budget', mobileLabel: 'Budget', icon: WalletIcon },
    { path: '/shopping-lists', label: 'Shopping Lists', mobileLabel: 'Lists', icon: ClipboardDocumentListIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="sticky top-0 z-30 bg-white/95 backdrop-blur shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <h1 className="text-xl sm:text-2xl font-bold text-blue-600">Zaphar</h1>
              </div>
              <div className="ml-8 hidden md:flex space-x-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive(link.path)
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* User Menu */}
            <div className="flex items-center space-x-4">
              <AccountSwitcher />
              {user && (
                <div className="relative" ref={userMenuRef}>
                  <button
                    type="button"
                    onClick={() => setIsUserMenuOpen((current) => !current)}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-gray-50 px-2 py-1.5 text-sm text-gray-700 transition hover:bg-gray-100"
                    aria-expanded={isUserMenuOpen}
                    aria-haspopup="menu"
                    aria-label="Open account menu"
                  >
                    <span
                      className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-[10px] font-semibold text-blue-700"
                      aria-hidden="true"
                    >
                      {initials}
                    </span>
                    <span className="hidden sm:inline">
                      {fullName}
                    </span>
                  </button>
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg" role="menu">
                      <div className="border-b border-gray-100 px-3 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-blue-700">
                            {initials}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-gray-900">{fullName}</p>
                            <p className="truncate text-xs text-gray-500">{user?.email || 'Signed in user'}</p>
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleOpenSharing}
                        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                        role="menuitem"
                      >
                        <UserGroupIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
                        Account sharing
                      </button>
                      <button
                        type="button"
                        onClick={handleOpenProfile}
                        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                        role="menuitem"
                      >
                        <UserIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
                        My Profile
                      </button>
                      <button
                        type="button"
                        onClick={handleOpenCategories}
                        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                        role="menuitem"
                      >
                        <TagIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
                        Categories
                      </button>
                      <button
                        type="button"
                        onClick={handleOpenSecurity}
                        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                        role="menuitem"
                      >
                        <ShieldCheckIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
                        Security
                      </button>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                        role="menuitem"
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8 pb-24 md:pb-8">
        {activeAccount && (
          <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-800 sm:hidden">
            Active: {activeAccount.isPersonal ? 'My Account' : activeAccount.name}
          </div>
        )}
        <div key={accountContextVersion}>{children}</div>
      </main>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-[80] border-t border-gray-200 bg-white/98 shadow-[0_-6px_14px_rgba(15,23,42,0.08)] backdrop-blur supports-[backdrop-filter]:bg-white/92">
        <div className="grid h-16 grid-cols-5">
          {navLinks.map((link) => {
            const Icon = link.icon;

            return (
              <Link
                key={link.path}
                to={link.path}
                className={`flex min-w-0 flex-col items-center justify-center gap-0.5 px-1 text-[10px] font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-blue-700'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                aria-current={isActive(link.path) ? 'page' : undefined}
              >
                <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                <span className="block max-w-full truncate text-center leading-none">{link.mobileLabel}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

export default Layout;
